import { useState } from 'react';
import logoFull from '@/assets/fluxo-logo-full.png';
import { useGoogleLogin } from '@react-oauth/google';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Bot, ShieldAlert, Cpu, Globe, Key, User, Loader2, Sparkles, Wand2 } from 'lucide-react';

const PRODUCT_NAME = 'Fluxo Co';

const LoginPage = () => {
  const { loginWithGoogle, loginWithEmail } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showDemoLogin, setShowDemoLogin] = useState(false);
  const [demoEmail, setDemoEmail] = useState('');
  const [password, setPassword] = useState('');

  const googleLogin = useGoogleLogin({
    onSuccess: async (response) => {
      setIsLoading(true);
      try {
        const accessToken = response.access_token;
        if (!accessToken) throw new Error('No se pudo obtener el token de Google.');
        const result = await loginWithGoogle(accessToken);
        if (result.success) {
          toast.success(`Acceso Maestro concedido`);
        } else {
          toast.error(result.error || 'Error al iniciar sesión');
        }
      } catch (err) {
        console.error('Error en autenticación Google:', err);
        toast.error(err.message || 'Error al autenticar.');
      } finally {
        setIsLoading(false);
      }
    },
    onError: (err) => {
      console.error('Google login error:', err);
      toast.error('Error de autenticación maestra.');
      setIsLoading(false);
    },
    flow: 'implicit',
  });

  const handleEmailLogin = async () => {
    if (!demoEmail || !password) {
      toast.error('Credenciales incompletas');
      return;
    }
    setIsLoading(true);
    const result = await loginWithEmail(demoEmail, password);
    if (result.success) {
      toast.success(`Bienvenido a la Consola de Control`);
    } else {
      toast.error(result.error);
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center p-6 overflow-hidden relative selection:bg-blue-500/30">
      {/* Premium Ambient Lights */}
      <div className="absolute top-[-10%] right-[-10%] w-[50%] h-[50%] bg-blue-600/10 blur-[120px] rounded-full animate-float" />
      <div className="absolute bottom-[-10%] left-[-10%] w-[50%] h-[50%] bg-indigo-600/10 blur-[120px] rounded-full animate-float" style={{ animationDelay: '-3s' }} />
      
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md z-10 space-y-8"
      >
        {/* Branding Section */}
        <div className="text-center space-y-4">
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="flex justify-center"
          >
            <div className="relative group">
              <div className="absolute inset-0 bg-blue-500/20 blur-2xl rounded-full group-hover:bg-blue-500/40 transition-all duration-500" />
              <img 
                src={logoFull} 
                alt="Fluxo Co" 
                className="h-32 w-auto relative z-10 object-contain invert hue-rotate-180 brightness-200 drop-shadow-[0_0_25px_rgba(16,185,129,0.4)]" 
              />
            </div>
          </motion.div>
          
          <div className="space-y-1">
            <motion.h1 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-4xl font-extrabold font-outfit tracking-tighter text-white uppercase"
            >
              MODO <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-300">MASTER</span>
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-[10px] text-slate-500 font-black uppercase tracking-[0.4em]"
            >
              Central Factory Portal
            </motion.p>
          </div>
        </div>

        {/* Login Card */}
        <div className="bg-[#0F172A]/40 backdrop-blur-3xl border border-white/5 p-8 rounded-[2.5rem] shadow-2xl relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent pointer-events-none" />
          
          {!showDemoLogin ? (
            <div className="space-y-6 relative z-10">
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-[10px] font-bold uppercase tracking-wider">
                  <ShieldAlert className="w-3 h-3" />
                  Autenticación Requerida
                </div>
              </div>

              <Button
                onClick={() => googleLogin()}
                disabled={isLoading}
                className="w-full h-14 bg-white hover:bg-slate-200 text-slate-900 font-bold rounded-2xl transition-all duration-300 flex items-center justify-center gap-4 shadow-xl shadow-white/5 group"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Globe className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                    Entrar con Google
                  </>
                )}
              </Button>

              <div className="relative py-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-white/5" />
                </div>
                <div className="relative flex justify-center text-[10px] uppercase tracking-[0.4em]">
                  <span className="bg-[#020617] px-6 text-emerald-500 font-black shadow-[0_0_15px_rgba(16,185,129,0.2)]">Nivel 2</span>
                </div>
              </div>

              <Button
                onClick={() => setShowDemoLogin(true)}
                variant="ghost"
                className="w-full h-12 text-slate-400 hover:text-emerald-400 border border-white/5 hover:border-emerald-500/30 bg-white/5 hover:bg-emerald-500/5 rounded-xl text-xs font-semibold tracking-wide transition-all duration-300"
              >
                Usar Credenciales Maestras
              </Button>
            </div>
          ) : (
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6 relative z-10"
            >
              <div className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-slate-500 text-[10px] font-bold uppercase tracking-widest pl-1">Identificador</Label>
                  <div className="relative group">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600 group-focus-within:text-blue-500 transition-colors" />
                    <Input
                      type="email"
                      value={demoEmail}
                      onChange={(e) => setDemoEmail(e.target.value)}
                      className="h-14 bg-slate-950/50 border-white/5 pl-12 rounded-2xl focus:border-blue-500/50 text-white placeholder:text-slate-700 transition-all"
                      placeholder="admin@fluxo.co"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-500 text-[10px] font-bold uppercase tracking-widest pl-1">Llave de Acceso</Label>
                  <div className="relative group">
                    <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600 group-focus-within:text-blue-500 transition-colors" />
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-14 bg-slate-950/50 border-white/5 pl-12 rounded-2xl focus:border-blue-500/50 text-white placeholder:text-slate-700 transition-all"
                      placeholder="••••••••"
                    />
                  </div>
                </div>
              </div>

              <Button
                onClick={handleEmailLogin}
                disabled={isLoading}
                className="w-full h-14 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-2xl shadow-xl shadow-blue-500/20 transition-all active:scale-[0.98] flex items-center justify-center gap-3"
              >
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                  <>
                    <Wand2 className="w-5 h-5" />
                    <span>Autenticar Consola</span>
                  </>
                )}
              </Button>

              <button 
                onClick={() => setShowDemoLogin(false)}
                className="w-full text-xs text-slate-400 hover:text-white font-bold uppercase tracking-widest transition-colors"
              >
                ← Volver a acceso global
              </button>
            </motion.div>
          )}
        </div>

        {/* Footer Monitor */}
        <div className="flex items-center justify-center gap-8 py-6">
          <div className="flex items-center gap-2 text-[10px] text-slate-400 font-bold uppercase tracking-widest hover:text-emerald-400 transition-colors cursor-default">
            <Cpu className="w-3.5 h-3.5 text-emerald-500" />
            <span>Stable Core v5.1</span>
          </div>
          <div className="flex items-center gap-2 text-[10px] text-slate-400 font-bold uppercase tracking-widest hover:text-emerald-400 transition-colors cursor-default">
            <ShieldAlert className="w-3.5 h-3.5 text-emerald-500 animate-pulse" />
            <span>Secure Tunnel Active</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
